import {
  createContext,
  DependencyList,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from '_common/hooks';
import { useParams } from 'react-router';
import LoadingPage from '_common/components/Loading/LoadingPage/LoadingPage';
import { ErrorScreen } from '_common/components/ErrorBoundary/ErrorBoundary';
import { PresentationManager } from './services/PresentationManager';
import { SessionStorage } from '_common/utils';
import { getPresentationInfo, setPresentationId } from './PresentationSlice';
import { closeModal } from 'App/ModalContext/utils';
import { openConnectionErrorModal, openDisconnectedModal } from './utils';
import { Errors } from '_common/services/Realtime/Transport';

const PresentationContext = createContext<PresentationManager | undefined>(undefined);

const PresentationManagerProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const userId = useSelector((state) => state.auth.userId);
  const token = SessionStorage.getToken();
  const [managerState, setManagerState] = useState<Presentation.Status>('NOT_READY');
  const [error, setError] = useState<any | null>(null);
  const [initializing, setInitializing] = useState(true);
  // const [loading, setLoading] = useState(true);

  const presentationManager = useRef<PresentationManager>(undefined);

  const handleError = useCallback((error: Error) => {
    setInitializing(false);
    if (error instanceof Errors.DisconnectedError) {
      openDisconnectedModal();
    } else if (error instanceof Errors.TransportError) {
      openConnectionErrorModal();
    } else {
      setError(error);
    }
  }, []);

  const statusChanged = useCallback((status: Presentation.Status) => {
    setManagerState(status);
    setError(null);
    if (status === 'READY') {
      closeModal('ConfirmationModal');
      setInitializing(false);
    } else if (status === 'DESTROYED' || status === 'DESTROYING') {
      setInitializing(false);
    }
  }, []);

  const loading = useCallback((loading: boolean, reason?: string) => {
    logger.info('loading...');
    // setLoading(loading);
  }, []);

  useEffect(() => {
    if (id && userId && token) {
      dispatch(setPresentationId(id));
      const request = dispatch(getPresentationInfo({ objectId: id, open: true }));
      request.then((response: any) => {
        if (response?.error && response.error.name !== 'AbortError') {
          setError(response.error);
        } else {
          setError(null);
        }

        const responseStatus = response?.payload?.status;

        if (responseStatus === 200) {
          if (!presentationManager.current) {
            presentationManager.current = PresentationManager.getInstance();
          }
          if (presentationManager.current && !presentationManager.current.isInitialized()) {
            presentationManager.current.on('STATUS_CHANGED', statusChanged);
            presentationManager.current.on('ERROR', handleError);
            presentationManager.current.on('LOADING', loading);
            presentationManager.current.connect(id, {
              id: userId,
              token,
            });
          }
        }
      });

      return () => {
        request?.abort();
        dispatch(setPresentationId(null));
        if (presentationManager.current) {
          presentationManager.current.destroy();
        }
      };
    }
  }, [id, userId, token]);

  return (
    <PresentationContext.Provider value={presentationManager.current}>
      {error && <ErrorScreen />}
      {!error && managerState === 'READY' && children}
      {!error && initializing && <LoadingPage />}
    </PresentationContext.Provider>
  );

  // return <PresentationContext.Provider value={undefined}>{children}</PresentationContext.Provider>;
};

export const usePresentationManager = () => {
  const context = useContext(PresentationContext);
  if (context === undefined) {
    throw new Error('usePresentationManager can only be used in a PresentationManagerProvider');
  }
  return context;
};

export function usePresentationDataEvents<
  E extends Presentation.Data.DataEventsEventName,
  H extends Presentation.Data.DataEvents[E],
>(type: E, listener: H, deps: DependencyList = []) {
  const manager = usePresentationManager();
  useEffect(() => {
    manager?.getData()?.events.on(type, listener);
    return () => {
      manager?.getData()?.events.off(type, listener);
    };
  }, [type, listener, ...deps]);
}

export function usePresentationEvents<
  E extends keyof Presentation.Events,
  H extends Presentation.Events[E],
>(type: E, listener: H, deps: DependencyList = []) {
  const manager = usePresentationManager();
  useEffect(() => {
    manager?.on(type, listener);
    return () => {
      manager?.off(type, listener);
    };
  }, [type, listener, ...deps]);
}

export default PresentationManagerProvider;
